// RequestDetailDrawer — the My Access detail card for an awaiting-approval
// request. Opens when an awaiting-approval card is clicked. Wraps the shared
// DetailCard with an approval-status card (who you're waiting on / who approved
// it + the request meta and a Nudge action) and a real per-request Activity
// timeline pulled from GET /members/access/requests/:id. The footer cancels the
// request. A draft waiting on the member's passkey signature shows "Needs your
// signature" and a Sign button instead of the approval state.

import {
  Box,
  Flex,
  HStack,
  Icon,
  Spinner,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import {
  TbBell,
  TbCheck,
  TbCircleCheck,
  TbCircleMinus,
  TbCircleX,
  TbShieldCheck,
  TbSignature,
} from "react-icons/tb";
import type { IconType } from "react-icons";

import Avatar from "components/ds/Avatar";
import ConfirmModal from "components/ds/ConfirmModal";
import PloyButton from "components/ds/PloyButton";
import UtilityButton from "components/ds/UtilityButton";
import SignedBadge from "components/passkeys/SignedBadge";
import { formatDuration } from "components/tasks/taskMeta";
import { appDisplayName } from "modules/apps/tenants";
import {
  MF_ACCESS,
  type MFAccessRequest,
  type MFMyActiveRequest,
} from "state/members/access_v2/catalogs";
import { needsSignature } from "state/members/stepUp";
import { useSignRequest } from "pages/Members/StepUp/useRequestSigning";
import { isRequestInProgress } from "state/members/access_v2/roleRequests";

import RequestLines from "pages/Members/Access/Catalogs/RequestLines";

import DetailCard, { fmtDate, fullName, MetaRow, sentLabel } from "./DetailCard";
import {
  REQUEST_STATUS_INFO,
  requestChipTone,
  toTimeline,
  type RequestTone,
} from "./detailLogic";

// The awaiting-approval rail's snapshot, with the status and lines kept live
// from the request itself, which can settle while the drawer is open.
type ShownRequest = Omit<MFMyActiveRequest, "status"> & {
  status: MFAccessRequest["status"];
};

const TONE_ICON: Record<RequestTone, IconType> = {
  warning: TbShieldCheck,
  success: TbCircleCheck,
  error: TbCircleX,
  neutral: TbCircleMinus,
};

// Draft card: the request has not gone anywhere yet. Its policy wants the
// requester to sign it with a passkey first.
const SignatureNeededCard = ({
  request: r,
  onSign,
  isStarting,
}: {
  request: ShownRequest;
  onSign: () => void;
  isStarting: boolean;
}) => (
  <Box
    bg="linear-gradient(to right, var(--chakra-colors-warning-50), var(--chakra-colors-neutral-25))"
    borderRadius="12px"
    boxShadow="elevLow"
    p="lg"
    display="flex"
    flexDirection="column"
    gap="md"
  >
    <HStack spacing="1xs">
      <Icon as={TbSignature} boxSize="18px" color="warning.600" />
      <Text fontSize="15px" fontWeight={600} color="warning.800">
        Needs your signature
      </Text>
    </HStack>
    <Text fontSize="smd" color="neutral.800">
      Your organisation asks you to sign this request with a passkey. It goes
      for approval once you sign.
    </Text>
    <Flex
      direction="column"
      gap="1xs"
      pt="md"
      borderTopWidth="1px"
      borderColor="blackAlpha.200"
    >
      <MetaRow label="Requested for">
        {formatDuration(r.requestedForSeconds, r.requestedIndefinite)}
      </MetaRow>
      <MetaRow label="For">
        {r.onBehalfOf ? fullName(r.onBehalfOf) || r.onBehalfOf.email : "Yourself"}
      </MetaRow>
      <MetaRow label="Started">{fmtDate(r.createdAt)}</MetaRow>
    </Flex>
    <Flex align="center" gap="sm" pt="2xs">
      <UtilityButton
        size="md"
        treatment="dropshadow"
        sentiment="azurite"
        isDisabled={isStarting}
        leftIcon={<Icon as={TbSignature} boxSize="14px" />}
        onClick={onSign}
      >
        Sign request
      </UtilityButton>
    </Flex>
  </Box>
);

// Approval-status card — the approval state plus the request meta and a nudge.
const ApprovalStatusCard = ({
  request: r,
}: {
  request: ShownRequest;
}) => {
  const toast = useToast();
  const { mutate: nudge, isLoading: nudging } = MF_ACCESS.useNudgeReviewers(r.id);
  const { sign, isStarting } = useSignRequest();
  const [justNudged, setJustNudged] = useState(false);

  if (needsSignature(r)) {
    return (
      <SignatureNeededCard
        request={r}
        onSign={() => sign(r.id)}
        isStarting={isStarting}
      />
    );
  }

  const approver = r.approvedBy ?? null;
  const isPending = r.status === "pending";
  const info = REQUEST_STATUS_INFO[r.status];
  const tint = info.tone;
  const gradient = `linear-gradient(to right, var(--chakra-colors-${tint}-50), var(--chakra-colors-neutral-25))`;

  const names = r.approvers.map((a) => fullName(a) || a.email).filter(Boolean);
  const waitingText =
    r.approvers.length === 0
      ? r.stageName || "the approval team"
      : r.approvers.length === 1
        ? names[0]
        : `${names[0]} +${r.approvers.length - 1} more`;

  // Reflect an existing nudge (24h server cooldown) so the button starts
  // disabled rather than bouncing off a cooldown error.
  const recentlyNudged = r.lastNudgedAt
    ? Date.now() - new Date(r.lastNudgedAt).getTime() < 24 * 60 * 60 * 1000
    : false;
  const isNudged = justNudged || recentlyNudged;

  const onBehalf = r.onBehalfOf;

  const doNudge = () => {
    setJustNudged(true);
    nudge()
      .then(() =>
        toast({ status: "success", variant: "subtle", title: "Reviewers nudged" }),
      )
      .catch(() => setJustNudged(false));
  };

  return (
    <Box
      bg={gradient}
      borderRadius="12px"
      boxShadow="elevLow"
      p="lg"
      display="flex"
      flexDirection="column"
      gap="md"
    >
      <Flex align="center" justify="space-between" gap="sm">
        <HStack spacing="1xs">
          <Icon as={TONE_ICON[tint]} boxSize="18px" color={`${tint}.600`} />
          <Text fontSize="15px" fontWeight={600} color={`${tint}.800`}>
            {info.heading}
          </Text>
        </HStack>
        <Text
          fontSize="xs"
          color="neutral.600"
          sx={{ fontVariantNumeric: "tabular-nums" }}
        >
          {sentLabel(r.createdAt)}
        </Text>
      </Flex>

      {isPending ? (
        <Flex align="center" gap="sm">
          <Flex align="center">
            {r.approvers.slice(0, 3).map((a, i) => (
              <Box
                key={a.id}
                ml={i === 0 ? 0 : "-8px"}
                position="relative"
                zIndex={r.approvers.length - i}
                borderRadius="full"
                borderWidth="2px"
                borderStyle="solid"
                borderColor="neutral.0"
                bg="neutral.0"
              >
                <Avatar size="sm" name={fullName(a) || a.email} src={a.imageUrl} />
              </Box>
            ))}
            {r.approvers.length === 0 && (
              <Avatar size="sm" name={r.stageName || "Approver"} ring />
            )}
          </Flex>
          <Text fontSize="smd" color="neutral.800">
            Waiting on{" "}
            <Box as="span" fontWeight={600} color="neutral.950">
              {waitingText}
            </Box>
          </Text>
        </Flex>
      ) : (
        <Flex direction="column" gap="md">
          {approver && (
            <Flex align="center" gap="sm">
              <Avatar
                size="sm"
                name={approver.name}
                src={approver.imageUrl ?? undefined}
                ring
              />
              <Text fontSize="smd" color="neutral.800">
                Approved by{" "}
                <Box as="span" fontWeight={600} color="neutral.950">
                  {approver.name}
                </Box>
              </Text>
            </Flex>
          )}
          {r.status === "provisioning" && (
            <Flex align="center" gap="1xs">
              <Spinner
                boxSize="13px"
                thickness="2px"
                speed="0.65s"
                color="azurite.600"
                emptyColor="neutral.200"
              />
              <Text fontSize="smd" color="neutral.700">
                Setting up your access…
              </Text>
            </Flex>
          )}
        </Flex>
      )}

      {r.lines?.length ? (
        <Flex
          direction="column"
          gap="1xs"
          pt="md"
          borderTopWidth="1px"
          borderColor="blackAlpha.200"
        >
          <Text fontSize="smd" color="neutral.700">
            Roles
          </Text>
          <RequestLines lines={r.lines} />
        </Flex>
      ) : null}

      {/* request meta */}
      <Flex
        direction="column"
        gap="1xs"
        pt="md"
        borderTopWidth="1px"
        borderColor="blackAlpha.200"
      >
        {!r.lines?.length && (
          <MetaRow label="Requested for">
            {formatDuration(r.requestedForSeconds, r.requestedIndefinite)}
          </MetaRow>
        )}
        <MetaRow label="For">
          {onBehalf ? fullName(onBehalf) || onBehalf.email : "Yourself"}
        </MetaRow>
        <MetaRow label="Submitted">{fmtDate(r.createdAt)}</MetaRow>
      </Flex>

      {isPending && (
        <Flex align="center" gap="sm" pt="2xs">
          <UtilityButton
            size="md"
            treatment="dropshadow"
            sentiment={isNudged ? "neutral" : "azurite"}
            isDisabled={isNudged || nudging}
            leftIcon={<Icon as={isNudged ? TbCheck : TbBell} boxSize="14px" />}
            onClick={doNudge}
          >
            {isNudged ? "Nudged" : "Nudge reviewers"}
          </UtilityButton>
        </Flex>
      )}
    </Box>
  );
};

const RequestDetailDrawer = ({
  request,
  onClose,
}: {
  request: MFMyActiveRequest | null;
  onClose: () => void;
}) => {
  const toast = useToast();
  const [shown, setShown] = useState<MFMyActiveRequest | null>(request);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  useEffect(() => {
    if (request) setShown(request);
  }, [request]);

  // Real per-request event log, status and lines — fetched lazily once the
  // drawer is open, and kept live while it is. suspense:false so loading it
  // never blanks the page behind the route fallback; the Activity section
  // simply appears when the data arrives.
  const detail = MF_ACCESS.useGetAccessRequest(shown?.id ?? null, {
    suspense: false,
    live: Boolean(request),
  });
  const { mutate: cancel, isLoading: cancelling } = MF_ACCESS.useCancelRequest(
    shown?.id ?? "",
  );

  const activity = useMemo(() => toTimeline(detail), [detail]);

  if (!shown) return null;

  const live: ShownRequest = {
    ...shown,
    status: detail?.status ?? shown.status,
    lines: detail?.lines ?? shown.lines,
  };
  const info = REQUEST_STATUS_INFO[live.status];

  const baseName =
    shown.selectableResource?.name ??
    shown.catalogItem?.name ??
    "Access request";
  const itemName = appDisplayName({ ...shown.selectableResource, name: baseName });
  const appName = shown.catalogItem?.name ?? shown.catalog?.name ?? "";
  const crumbs = Array.from(
    new Set(
      [shown.catalog?.name, shown.catalogItem?.name].filter((s): s is string =>
        Boolean(s),
      ),
    ),
  ).filter((s) => s !== baseName);
  const logoUrl =
    shown.catalogItem?.imageUrl || shown.selectableResource?.imageUrl;

  const doCancel = () => {
    cancel()
      .then(() => {
        toast({ status: "success", variant: "subtle", title: "Request cancelled" });
        onClose();
      })
      .catch(() =>
        toast({
          status: "error",
          variant: "subtle",
          title: "Couldn't cancel request",
        }),
      );
  };

  const footer = (
    <>
      {isRequestInProgress(live.status) && (
        <PloyButton
          variant="danger"
          isDisabled={cancelling}
          onClick={() => setConfirmingCancel(true)}
        >
          Cancel request
        </PloyButton>
      )}
      <PloyButton variant="secondary" ml="auto" onClick={onClose}>
        Close
      </PloyButton>
    </>
  );

  return (
    <>
      <DetailCard
        // Step aside while the cancel confirmation is up: two stacked overlays
        // read as competing modals (and the drawer's own blurred overlay was
        // washing the dialog out). Stay away while the cancellation is in
        // flight too, so confirming doesn't slide the drawer back in for the
        // length of the request. Dismissing the confirmation (or a failed
        // cancel) brings it straight back, since `request` is untouched.
        isOpen={Boolean(request) && !confirmingCancel && !cancelling}
        // Stepping aside is not really closing: keep the focus Chakra would
        // otherwise pull back to the row that opened the drawer, so the
        // confirmation keeps it and Enter still confirms.
        returnFocusOnClose={!confirmingCancel && !cancelling}
        onClose={onClose}
        header={{
          itemName,
          crumbs,
          logoUrl,
          statusChip: {
            tone: requestChipTone(info.tone),
            label: info.chip,
          },
          badge: <SignedBadge signatures={detail?.signatures ?? shown.signatures} />,
        }}
        statusLabel="Approval status"
        statusSlot={<ApprovalStatusCard request={live} />}
        history={{
          priorRequests: shown.priorRequests,
          teamAdoption: shown.teamAdoption,
          catalogItemName: appName,
          catalogName: shown.catalog?.name,
        }}
        activity={activity}
        footer={footer}
      />

      {confirmingCancel && (
        <ConfirmModal
          title="Cancel this request?"
          body={`This withdraws your access request for ${itemName}. You can request it again later.`}
          confirmLabel="Cancel request"
          // "Cancel" next to "Cancel request" reads as two ways to cancel the
          // request; name the dismiss action after what it actually does.
          cancelLabel="Keep request"
          confirmTone="danger"
          onConfirm={() => {
            setConfirmingCancel(false);
            doCancel();
          }}
          onCancel={() => setConfirmingCancel(false)}
        />
      )}
    </>
  );
};

export default RequestDetailDrawer;
